import { Option, TableRow } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';

import { DocumentUploadClientInfoResponse } from 'src/front/api/generated';
import { UploadDocumentFormData } from 'src/front/components/document/UploadDocumentModal';
import {
  CreditTableRow,
  UploadDocumentPageData,
} from 'src/front/pages/applications/upload-document/UploadDocumentSearch';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  CLEAR,
  SET_DOCUMENTS,
  SET_CLIENT_INFO,
  SET_CREDIT_INFO,
}

type Action =
  | {
      type: ACTIONS.SEARCH_CUSTOMER;
      payload: {
        customerSearchValues?: CustomerSearchValues;
        customerPageNumber?: number;
        cardPageNumber?: number;
        creditPageNumber?: number;
      };
    }
  | { type: ACTIONS.CLEAR }
  | { type: ACTIONS.SET_DOCUMENTS; payload: { documents?: UploadDocumentFormData[] } }
  | { type: ACTIONS.SET_CLIENT_INFO; payload: { clientInfo?: DocumentUploadClientInfoResponse } }
  | { type: ACTIONS.SET_CREDIT_INFO; payload: { creditInfo?: UploadDocumentPageData } };

interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  customerNumber: string;
  birthDate: Date | null;
  selectedCustomer?: TableRow;
  selectedCard?: TableRow;
  selectedCredit?: CreditTableRow;
  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}

interface UploadDocumentState {
  documentTypeValue?: Option | null;
  customerSearchValues?: CustomerSearchValues;
  documents?: UploadDocumentFormData[];
  clientInfo?: DocumentUploadClientInfoResponse;
  creditInfo?: UploadDocumentPageData;
  customerPageNumber?: number;
  cardPageNumber?: number;
  creditPageNumber?: number;
}

const initialValues = {};

type UploadDocumentDispatch = (action: Action) => void;

const UploadDocumentStateContext = createContext<UploadDocumentState | undefined>(undefined);
const UploadDocumentDispatchContext = createContext<UploadDocumentDispatch | undefined>(undefined);

const uploadDocumentReducer = (state: UploadDocumentState, action: Action): UploadDocumentState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.SET_DOCUMENTS:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.SET_CLIENT_INFO:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const UploadDocumentProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(uploadDocumentReducer, initialValues);

  return (
    <UploadDocumentStateContext.Provider value={state}>
      <UploadDocumentDispatchContext.Provider value={dispatch}>
        {children}
      </UploadDocumentDispatchContext.Provider>
    </UploadDocumentStateContext.Provider>
  );
};

export const useCustomerSearchState = (): UploadDocumentState => {
  const context = useContext(UploadDocumentStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a UploadDocumentProvider');
  }

  return context;
};

type SearchInput = Pick<
  UploadDocumentState,
  | 'customerSearchValues'
  | 'documentTypeValue'
  | 'customerPageNumber'
  | 'cardPageNumber'
  | 'creditPageNumber'
  | 'creditInfo'
>;

type Documents = Pick<UploadDocumentState, 'documents'>;
type ClientInfo = Pick<UploadDocumentState, 'clientInfo'>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(UploadDocumentDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a UploadDocumentProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useSetDocumentsDispatch = (): ((payload: Documents) => void) => {
  const dispatch = useContext(UploadDocumentDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetDocumentsDispatch must be used within a UploadDocumentProvider');
  }

  return (payload: Documents) => dispatch({ type: ACTIONS.SET_DOCUMENTS, payload });
};

export const useSetClientInfoDispatch = (): ((payload: ClientInfo) => void) => {
  const dispatch = useContext(UploadDocumentDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetClientInfoDispatch must be used within a UploadDocumentProvider');
  }

  return (payload: ClientInfo) => dispatch({ type: ACTIONS.SET_CLIENT_INFO, payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(UploadDocumentDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a UploadDocumentProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default UploadDocumentProvider;
